import { showErrorMessage } from 'components/Helpers/Toasts';
import { getUserFriendlyErrors } from 'connectors/lib/friendlyErrorMessageParser';
import { Kind } from 'graphql';
import {
    AddProductToWishlistMutationDocumentApi,
    CartQueryDocumentApi,
    CleanWishlistMutationDocumentApi,
    RemoveProductFromWishlistMutationDocumentApi,
    WishlistQueryDocumentApi,
} from 'graphql/generated';
import { isFlashMessageError, isNoLogError } from 'helpers/errors/applicationErrors';
import { isWithErrorDebugging, isWithToastAndConsoleErrorDebugging } from 'helpers/errors/isWithErrorDebugging';
import { logException } from 'helpers/errors/logException';
import { mapGraphqlErrorForDevelopment } from 'helpers/errors/mapGraphqlErrorForDevelopment';
import { isClient } from 'helpers/isServer';
import { handleCartError } from 'hooks/cart/useCurrentCart';
import { GetServerSidePropsContext, NextPageContext } from 'next';
import { Translate } from 'next-translate';
import { usePersistStore } from 'store/usePersistStore';
import { CombinedError, Exchange, Operation } from 'urql';
import { removeTokensFromCookies } from 'utils/Auth/TokensFromCookies';
import { pipe, tap } from 'wonka';

export const getErrorExchange =
    (t: Translate, context?: GetServerSidePropsContext | NextPageContext): Exchange =>
    ({ forward }) => {
        return (operations$) => {
            return pipe(
                operations$,
                forward,
                tap(({ error, operation }) => {
                    if ((operation.kind !== 'query' && operation.kind !== 'mutation') || !error) {
                        return;
                    }

                    if (isWithErrorDebugging && operation.kind === 'mutation') {
                        handleErrorMessagesForMutation(error);

                        return;
                    }

                    if (isClient && hasFriendlyUrlQueryFailedWith500(operation, error)) {
                        if (isWithErrorDebugging) {
                            throw new Error(JSON.stringify(mapGraphqlErrorForDevelopment(error.graphQLErrors[0])));
                        }

                        throw new Error('Internal Server Error');
                    }

                    const isAuthError = error.response?.status === 401;
                    if (isAuthError) {
                        removeTokensFromCookies(context);

                        return;
                    }

                    if (isWithErrorDebugging) {
                        handleErrorMessagesForDevelopment(error);
                    } else {
                        handleErrorMessagesForUsers(error, t, operation);
                    }
                }),
            );
        };
    };

const handleErrorMessagesForDevelopment = (error: CombinedError) => {
    logException({
        message: error.message,
        originalError: JSON.stringify(error),
        location: 'getErrorExchange.handleErrorMessagesForDevelopment',
    });

    if (isWithToastAndConsoleErrorDebugging) {
        error.graphQLErrors
            .map((graphqlError) => mapGraphqlErrorForDevelopment(graphqlError))
            .forEach((simplifiedGraphqlError) => showErrorMessage(JSON.stringify(simplifiedGraphqlError)));
    }
};

const handleErrorMessagesForMutation = (error: CombinedError) => {
    logException({
        message: error.message,
        originalError: JSON.stringify(error),
        location: 'getErrorExchange.handleErrorMessagesForMutation',
    });

    if (isWithToastAndConsoleErrorDebugging) {
        error.graphQLErrors
            .map((graphqlError) => mapGraphqlErrorForDevelopment(graphqlError))
            .forEach((simplifiedGraphqlError) => showErrorMessage(JSON.stringify(simplifiedGraphqlError)));
    }
};

const handleErrorMessagesForUsers = (error: CombinedError, t: Translate, operation: Operation) => {
    const parsedErrors = getUserFriendlyErrors(error, t);
    const isCartError = operation.query === CartQueryDocumentApi;

    if (parsedErrors.userError) {
        logException({
            message: error.message,
            parsedUserError: parsedErrors.userError,
            originalError: JSON.stringify(error),
            location: 'getErrorExchange.handleErrorMessagesForUsers',
        });
    }

    if (isCartError) {
        handleCartError(error, t);

        return;
    }

    const isWishlistOperation = [
        WishlistQueryDocumentApi,
        AddProductToWishlistMutationDocumentApi,
        RemoveProductFromWishlistMutationDocumentApi,
        CleanWishlistMutationDocumentApi,
    ].includes(operation.query);

    if (isWishlistOperation) {
        return;
    }

    if (!parsedErrors.applicationError) {
        return;
    }

    if (!isNoLogError(parsedErrors.applicationError.type)) {
        logException({
            message: error.message,
            parsedApplicationError: parsedErrors.applicationError,
            originalError: JSON.stringify(error),
            location: 'getErrorExchange.handleErrorMessagesForUsers',
        });
    }

    const isLogoutInProgress =
        typeof window !== 'undefined' && usePersistStore.getState().authLoading === 'logout-loading';

    if (isFlashMessageError(parsedErrors.applicationError.type) && !isLogoutInProgress) {
        showErrorMessage(parsedErrors.applicationError.message);
    }
};

const hasFriendlyUrlQueryFailedWith500 = (operation: Operation, error: CombinedError) =>
    error.graphQLErrors.some(({ extensions }) => extensions.code === 500) &&
    operation.query.definitions.some(
        (definition) =>
            definition.kind === Kind.OPERATION_DEFINITION &&
            definition.directives?.some((directiveDefinition) => directiveDefinition.name.value === 'friendlyUrl'),
    );
